import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft, MessageSquare, Check, XCircle, Sparkles, Loader2,
  RefreshCw, History, Send, Share2, Copy,
  Crown, DollarSign, Cpu, Workflow, Scale, ShieldAlert, Megaphone,
  Users, BarChart3, BookCheck, HeartHandshake, ShieldCheck, Radar,
  GraduationCap, Library, HeartPulse, MessagesSquare,
  Anchor, Stethoscope, Hammer, Plane, Factory, Sprout, School, Zap,
} from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

const ICON_MAP = {
  Crown, DollarSign, Cpu, Workflow, Scale, ShieldAlert, Megaphone,
  Users, BarChart3, BookCheck, HeartHandshake, ShieldCheck, Radar,
  GraduationCap, Library, HeartPulse, MessagesSquare,
  Anchor, Stethoscope, Hammer, Plane, Factory, Sprout, School, Zap,
};

/* -------------------------------------------------------------------------- */
/*  Council Session Detail                                                    */
/*  GET /api/ai/council/sessions/{council_id}                                 */
/*  Renders question, members, moderator, synthesis, dissents, next actions,  */
/*  and the full per-persona transcript.                                      */
/* -------------------------------------------------------------------------- */

function fmtDateTime(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString(); }
  catch { return iso; }
}

export default function CouncilSessionDetail() {
  const { councilId } = useParams();
  const [personas, setPersonas] = useState([]);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Follow-up round form state
  const [showForm, setShowForm] = useState(false);
  const [followUp, setFollowUp] = useState('');
  const [target, setTarget] = useState('dissenters');
  const [convening, setConvening] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get('/ai/employees/').then(({ data }) => data),
      api.get(`/ai/council/sessions/${councilId}`).then(({ data }) => data).catch((e) => {
        const detail = e?.response?.data?.detail || 'Could not load session.';
        setError(detail);
        toast.error(detail);
        return null;
      }),
    ])
      .then(([emp, ses]) => {
        setPersonas(emp);
        setSession(ses);
      })
      .finally(() => setLoading(false));
  }, [councilId]);

  const personaById = (id) => personas.find((p) => p.id === id);
  const personaName = (id) => personaById(id)?.english_name || id;

  const reopen = async () => {
    setConvening(true);
    try {
      const { data } = await api.post(`/ai/council/sessions/${councilId}/round`, {
        follow_up: followUp,
        target,
      });
      setSession(data);
      setShowForm(false);
      setFollowUp('');
      toast.success(`Round ${data.rounds} complete — ${data.answers?.length || 0} voices re-synthesised.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not run follow-up round.');
    } finally {
      setConvening(false);
    }
  };

  const [shareLink, setShareLink] = useState('');
  const [sharing, setSharing] = useState(false);

  const generateShareLink = async () => {
    setSharing(true);
    try {
      const { data } = await api.post(`/ai/council/sessions/${councilId}/share`);
      const url = `${window.location.origin}/observe/council/${councilId}?token=${data.token}`;
      setShareLink(url);
      try {
        await navigator.clipboard.writeText(url);
        toast.success(`Read-only link copied — valid ${data.expires_in_hours}h.`);
      } catch {
        toast.success(`Read-only link minted — valid ${data.expires_in_hours}h.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not mint share link.');
    } finally { setSharing(false); }
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(shareLink); toast.success('Copied.'); }
    catch { /* clipboard blocked */ }
  };

  if (loading) {
    return (
      <div className="ve-section">
        <div className="inline-flex items-center gap-2 text-sm text-slate-500" data-testid="council-detail-loading">
          <Loader2 size={14} className="animate-spin" /> Loading deliberation…
        </div>
      </div>
    );
  }

  if (error || !session) {
    return (
      <div className="ve-section">
        <Link to="/dashboard/ai-council" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-[#5E1F8B] font-bold uppercase tracking-[0.18em]">
          <ArrowLeft size={12} /> Back to Council
        </Link>
        <div className="mt-6 ve-card p-8 text-center" data-testid="council-detail-error">
          <XCircle size={28} className="text-rose-400 mx-auto" />
          <p className="mt-3 text-sm text-slate-600 dark:text-white/65">{error || 'Session not found.'}</p>
        </div>
      </div>
    );
  }

  const moderator = personaById(session.moderator);
  const synth = session.synthesis || {};

  return (
    <div className="ve-section" data-testid="council-detail-page">
      <Link
        to="/dashboard/ai-council"
        className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-[#5E1F8B] font-bold uppercase tracking-[0.18em]"
        data-testid="council-detail-back"
      >
        <ArrowLeft size={12} /> Back to Council
      </Link>

      {/* Header */}
      <div className="mt-4 ve-card p-5 lg:p-6">
        <div className="ve-eyebrow text-[#5E1F8B] dark:text-[#8B3FC5] flex items-center gap-2">
          <MessageSquare size={12} /> Council session · {fmtDateTime(session.created_at)}
        </div>
        <h1 className="display text-2xl lg:text-3xl mt-2 font-bold leading-snug">{session.question}</h1>

        <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-3">
          {/* Moderator */}
          <div className="flex items-center gap-2.5">
            <div className="text-[10px] uppercase tracking-[0.22em] text-slate-400 dark:text-white/45 font-bold">Moderator</div>
            {moderator ? (() => {
              const Icon = ICON_MAP[moderator.icon] || Sparkles;
              return (
                <Link
                  to={`/dashboard/ai-employees/${moderator.id}`}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded border border-[#8B3FC5] bg-[#FFF8E5] text-[#7A5A00] dark:bg-[#3a2f0a] dark:text-[#F5E1A4] text-xs font-bold hover:bg-[#8B3FC5] hover:text-white transition"
                >
                  <Icon size={11} /> {moderator.english_name}
                </Link>
              );
            })() : <span className="text-xs">{session.moderator}</span>}
          </div>

          {/* Members count */}
          <div className="flex items-center gap-2">
            <div className="text-[10px] uppercase tracking-[0.22em] text-slate-400 dark:text-white/45 font-bold">Members</div>
            <span className="text-sm font-bold text-slate-900 dark:text-white">{(session.members || []).length}</span>
          </div>

          {/* Rounds */}
          {session.rounds && (
            <div className="flex items-center gap-2">
              <div className="text-[10px] uppercase tracking-[0.22em] text-slate-400 dark:text-white/45 font-bold">Rounds</div>
              <span className="text-sm font-bold text-slate-900 dark:text-white">{session.rounds}</span>
            </div>
          )}
        </div>

        {/* Member chips */}
        <div className="mt-4 flex flex-wrap gap-2">
          {(session.members || []).map((m) => {
            const p = personaById(m);
            const Icon = p ? (ICON_MAP[p.icon] || Sparkles) : Sparkles;
            return (
              <Link
                key={m}
                to={`/dashboard/ai-employees/${m}`}
                className="inline-flex items-center gap-1.5 px-2 py-1 rounded border border-slate-200 dark:border-white/10 text-[10px] uppercase tracking-[0.18em] font-bold text-slate-700 dark:text-white/75 hover:border-[#8B3FC5] hover:text-[#5E1F8B] dark:hover:text-[#8B3FC5] transition"
                data-testid={`council-detail-member-${m}`}
              >
                <Icon size={10} />{personaName(m)}
              </Link>
            );
          })}
        </div>
      </div>

      {/* Synthesis */}
      <div className="mt-4 ve-card p-5 lg:p-6" data-testid="council-detail-synthesis">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="ve-eyebrow text-[#5E1F8B] dark:text-[#8B3FC5]">
            Synthesis{session.rounds > 1 && <span className="ml-2 text-slate-400 dark:text-white/35 normal-case tracking-normal">· after {session.rounds} rounds</span>}
          </div>
          <button
            onClick={() => setShowForm((v) => !v)}
            data-testid="council-detail-reopen-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-[#8B3FC5]/50 text-[10px] uppercase tracking-[0.22em] font-bold text-[#5E1F8B] dark:text-[#8B3FC5] hover:bg-[#FFF8E5] dark:hover:bg-[#3a2f0a] transition"
          >
            <RefreshCw size={11} /> {showForm ? 'Cancel' : 'Re-open with another round'}
          </button>
          <button
            onClick={generateShareLink}
            disabled={sharing}
            data-testid="council-detail-share-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-slate-200 dark:border-white/15 text-[10px] uppercase tracking-[0.22em] font-bold text-slate-600 dark:text-white/70 hover:border-[#8B3FC5]/60 hover:text-[#5E1F8B] dark:hover:text-[#8B3FC5] transition"
          >
            <Share2 size={11} /> {sharing ? 'Minting…' : 'Share read-only link'}
          </button>
        </div>
        {synth.position ? (
          <h2 className="display text-xl lg:text-2xl mt-2 font-bold leading-snug">{synth.position}</h2>
        ) : (
          <p className="mt-2 text-sm text-slate-500 dark:text-white/55 italic">No final position synthesised.</p>
        )}
        {synth.changes_from_prior && (
          <p className="mt-2 text-xs italic text-slate-500 dark:text-white/55 border-l-2 border-[#8B3FC5]/40 pl-3">
            Changed from prior: {synth.changes_from_prior}
          </p>
        )}

        {Array.isArray(synth.rationale) && synth.rationale.length > 0 && (
          <ul className="mt-4 space-y-2">
            {synth.rationale.map((r, i) => (
              <li key={i} className="text-sm flex gap-2"><span className="text-[#5E1F8B] dark:text-[#8B3FC5] shrink-0">·</span><span>{r}</span></li>
            ))}
          </ul>
        )}

        {Array.isArray(synth.dissents) && synth.dissents.length > 0 && (
          <div className="mt-6">
            <div className="ve-eyebrow text-rose-600">Dissents</div>
            <div className="mt-2 space-y-2">
              {synth.dissents.map((d, i) => (
                <div key={i} className="rounded-md bg-rose-50 dark:bg-rose-900/10 border border-rose-200 dark:border-rose-400/20 p-3 text-sm" data-testid={`council-detail-dissent-${i}`}>
                  <b>{personaName(d.persona)}:</b> {d.objection}
                </div>
              ))}
            </div>
          </div>
        )}

        {Array.isArray(synth.next_actions) && synth.next_actions.length > 0 && (
          <div className="mt-6">
            <div className="ve-eyebrow text-emerald-600 dark:text-emerald-400">Next actions</div>
            <ul className="mt-2 space-y-1.5">
              {synth.next_actions.map((a, i) => (
                <li key={i} className="text-sm flex gap-2"><Check size={14} className="text-emerald-600 dark:text-emerald-400 mt-0.5 shrink-0" /><span>{a}</span></li>
              ))}
            </ul>
          </div>
        )}

        {/* Share-link display (after first generation) */}
        {shareLink && (
          <div className="mt-4 rounded-md border border-[#8B3FC5]/40 bg-[#FFF8E5] dark:bg-[#3a2f0a]/50 p-3" data-testid="council-detail-share-link">
            <div className="flex items-center gap-2 flex-wrap">
              <Share2 size={12} className="text-[#5E1F8B] dark:text-[#8B3FC5]" />
              <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#7A5A00] dark:text-[#F5E1A4]">Read-only observer link</span>
              <span className="text-[10px] text-slate-500 dark:text-white/55">expires 24h</span>
            </div>
            <div className="mt-2 flex items-center gap-2">
              <input
                readOnly
                value={shareLink}
                onClick={(e) => e.target.select()}
                data-testid="council-detail-share-url"
                className="flex-1 min-w-0 px-2 py-1.5 text-[11px] font-mono rounded border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 focus:outline-none focus:border-[#8B3FC5]"
              />
              <button
                onClick={copyLink}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[10px] uppercase tracking-[0.22em] font-bold rounded bg-[#8B3FC5] text-white hover:bg-[#AE52EA] transition"
              >
                <Copy size={11} /> Copy
              </button>
            </div>
            <p className="mt-2 text-[10px] text-slate-500 dark:text-white/55">
              Anyone with this link can watch this council deliberate (auto-refreshing) — they cannot re-open the question.
            </p>
          </div>
        )}

        {/* Follow-up round form */}
        {showForm && (
          <div className="mt-6 pt-5 border-t border-slate-200 dark:border-white/10" data-testid="council-detail-followup-form">
            <div className="ve-eyebrow text-[#5E1F8B] dark:text-[#8B3FC5]">Convene another round</div>
            <p className="text-xs text-slate-500 dark:text-white/55 mt-1">
              The principal types one more framing. {Array.isArray(synth.dissents) && synth.dissents.length > 0 ? `The ${synth.dissents.length} dissenter(s)` : 'All members'} re-vote, then {personaName(session.moderator)} re-synthesises. Costs one LLM call per voice + one for the synthesis.
            </p>

            <textarea
              value={followUp}
              onChange={(e) => setFollowUp(e.target.value)}
              data-testid="council-detail-followup-text"
              rows={3}
              placeholder="e.g. 'The CFO has tabled a new 18-month runway model — does this change the readiness verdict?' (optional)"
              className="mt-3 w-full rounded-md border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 px-4 py-3 text-sm focus:outline-none focus:border-[#8B3FC5] dark:focus:border-[#8B3FC5]"
            />

            <div className="mt-3 flex flex-wrap items-center gap-4">
              <div className="text-[10px] uppercase tracking-[0.22em] text-slate-400 dark:text-white/45 font-bold">Ask</div>
              <label className="inline-flex items-center gap-1.5 text-sm cursor-pointer">
                <input
                  type="radio"
                  name="council-followup-target"
                  value="dissenters"
                  checked={target === 'dissenters'}
                  onChange={() => setTarget('dissenters')}
                  data-testid="council-detail-target-dissenters"
                  disabled={!Array.isArray(synth.dissents) || synth.dissents.length === 0}
                  className="accent-[#8B3FC5]"
                />
                <span>Dissenters only ({synth.dissents?.length || 0})</span>
              </label>
              <label className="inline-flex items-center gap-1.5 text-sm cursor-pointer">
                <input
                  type="radio"
                  name="council-followup-target"
                  value="all"
                  checked={target === 'all'}
                  onChange={() => setTarget('all')}
                  data-testid="council-detail-target-all"
                  className="accent-[#8B3FC5]"
                />
                <span>All members ({(session.members || []).length})</span>
              </label>
              <button
                onClick={reopen}
                disabled={convening}
                data-testid="council-detail-reopen-submit"
                className="ml-auto ve-btn-primary text-sm disabled:opacity-50 inline-flex items-center gap-2"
              >
                {convening ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                {convening ? 'Convening…' : 'Convene round'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Synthesis history — appears once at least one follow-up round has run */}
      {Array.isArray(session.synthesis_history) && session.synthesis_history.length > 0 && (
        <div className="mt-4 ve-card p-5 lg:p-6" data-testid="council-detail-history">
          <div className="ve-eyebrow text-slate-500 dark:text-white/55 flex items-center gap-2">
            <History size={12} /> Synthesis history · {session.synthesis_history.length} prior decision{session.synthesis_history.length === 1 ? '' : 's'}
          </div>
          <ol className="mt-3 relative border-l-2 border-slate-200 dark:border-white/10 ml-2 space-y-3">
            {session.synthesis_history.map((entry, i) => {
              const s = entry.synthesis || {};
              return (
                <li
                  key={i}
                  className="pl-4 relative"
                  data-testid={`council-detail-history-${entry.round}`}
                >
                  <span className="absolute -left-[7px] top-2 h-3 w-3 rounded-full bg-slate-300 dark:bg-white/15" />
                  <div className="rounded-md border border-slate-200 dark:border-white/10 p-3">
                    <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-400 dark:text-white/45">
                      Round {entry.round} · archived {fmtDateTime(entry.archived_at)}
                    </div>
                    {s.position && (
                      <p className="mt-1 text-sm text-slate-700 dark:text-white/75 line-through decoration-slate-400 decoration-1">
                        {s.position}
                      </p>
                    )}
                    {Array.isArray(s.dissents) && s.dissents.length > 0 && (
                      <div className="mt-2 text-[11px] text-rose-600 dark:text-rose-400">
                        {s.dissents.length} dissent{s.dissents.length === 1 ? '' : 's'} at the time: {s.dissents.map((d) => personaName(d.persona)).join(', ')}
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
          {Array.isArray(session.additional_rounds_log) && session.additional_rounds_log.length > 0 && (
            <div className="mt-4 text-[11px] text-slate-400 dark:text-white/45">
              Last re-convened {fmtDateTime(session.additional_rounds_log[session.additional_rounds_log.length - 1]?.convened_at)}
              {session.additional_rounds_log[session.additional_rounds_log.length - 1]?.follow_up && (
                <span className="block mt-1 italic">
                  Follow-up: &ldquo;{session.additional_rounds_log[session.additional_rounds_log.length - 1].follow_up}&rdquo;
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {/* Per-persona transcript */}
      <div className="mt-4">
        <div className="ve-eyebrow text-slate-500 dark:text-white/55">
          {session.rounds > 1 ? `Latest round transcript · round ${session.rounds}` : 'Full transcript'} · {(session.answers || []).length} voices
        </div>
        <div className="mt-3 space-y-3">
          {(session.answers || []).map((a, idx) => {
            const p = personaById(a.persona);
            const Icon = p ? (ICON_MAP[p.icon] || Sparkles) : Sparkles;
            return (
              <div key={`${a.persona}-${idx}`} className="ve-card p-4 lg:p-5" data-testid={`council-detail-answer-${a.persona}`}>
                <div className="flex items-start gap-3">
                  <div className="h-10 w-10 rounded-full border border-[#8B3FC5]/40 bg-black flex items-center justify-center shrink-0">
                    <Icon size={18} className="text-[#8B3FC5]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <Link to={`/dashboard/ai-employees/${a.persona}`} className="text-sm font-bold hover:text-[#5E1F8B] dark:hover:text-[#8B3FC5] transition">
                      {personaName(a.persona)}
                    </Link>
                    {p?.english_role && (
                      <div className="text-[10px] uppercase tracking-[0.22em] text-slate-400 dark:text-white/45 font-bold mt-0.5">{p.english_role}</div>
                    )}
                    <p className="mt-2 text-sm text-slate-700 dark:text-white/80 whitespace-pre-wrap leading-relaxed">{a.answer}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ─── Routing Receipt — provenance for every LLM call. v90. ─── */}
      <RoutingReceiptCard session={session} />
    </div>
  );
}

function RoutingReceiptCard({ session }) {
  const log = Array.isArray(session?.routing_log) ? session.routing_log : [];
  const summary = session?.routing_summary || null;
  if (log.length === 0 && !summary) return null;
  const allBedrock = !!summary?.all_bedrock;
  const totalMs = summary?.total_ms ?? log.reduce((s, r) => s + (r.latency_ms || 0), 0);
  const avgMs = summary?.avg_ms_per_call ?? (log.length ? Math.round(totalMs / log.length) : 0);
  const byProvider = summary?.by_provider || log.reduce((acc, r) => {
    const p = r.provider || 'unknown';
    acc[p] = (acc[p] || 0) + 1;
    return acc;
  }, {});
  return (
    <div className="mt-4 ve-card p-5 lg:p-6" data-testid="council-routing-receipt">
      <div className="ve-eyebrow flex items-center justify-between">
        <span>Routing Receipt</span>
        <span className={`text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full border ${allBedrock ? 'border-emerald-400/40 bg-emerald-500/10 text-emerald-300' : 'border-[var(--brand-primary-strong)] bg-[var(--brand-primary-soft)] text-[var(--brand-primary)]'}`}>
          {allBedrock ? 'INSIDE YOUR AWS' : 'MIXED · VE GATEWAY'}
        </span>
      </div>
      <div className="mt-2 text-sm text-slate-600 dark:text-white/65">
        Provenance for every LLM call in this deliberation. Use this to prove data-sovereignty when audited, and to spot slow rounds.
      </div>
      <div className="mt-4 flex flex-wrap gap-4 text-xs">
        <div><span className="opacity-60">Total calls </span><span className="font-bold pal-mono">{log.length}</span></div>
        <div><span className="opacity-60">Total time </span><span className="font-bold pal-mono">{totalMs} ms</span></div>
        <div><span className="opacity-60">Avg / call </span><span className="font-bold pal-mono">{avgMs} ms</span></div>
        {(summary?.regions || []).length > 0 && <div><span className="opacity-60">Regions </span><span className="font-bold pal-mono">{(summary?.regions || []).join(', ')}</span></div>}
      </div>
      <div className="mt-3 flex flex-wrap gap-1.5">
        {Object.entries(byProvider).map(([prov, count]) => {
          const isBedrock = prov === 'bedrock';
          const isGateway = ['ve_gateway', 'anthropic', 'gemini', 'openai'].includes(prov);
          const cls = isBedrock
            ? 'border-emerald-400/40 text-emerald-300'
            : isGateway
              ? 'border-[var(--brand-primary-strong)] text-[var(--brand-primary)]'
              : 'border-rose-400/40 text-rose-300';
          // Map the backend's internal provider key to a customer-facing label.
          const displayName = prov === 've_gateway' ? 'VE Gateway' : prov;
          return (
            <span key={prov} className={`pal-mono text-[10px] uppercase tracking-[0.15em] font-bold px-2 py-0.5 rounded border ${cls}`}>
              {displayName} · {count}
            </span>
          );
        })}
      </div>
      <details className="mt-4">
        <summary className="cursor-pointer text-[11px] uppercase tracking-[0.18em] font-bold opacity-65 hover:opacity-100">Per-call detail ({log.length})</summary>
        <table className="w-full mt-3 text-xs">
          <thead>
            <tr className="text-[9px] uppercase tracking-[0.18em] text-slate-400 dark:text-white/40">
              <th className="text-left pb-1.5 font-bold">Persona</th>
              <th className="text-left pb-1.5 font-bold">Provider</th>
              <th className="text-left pb-1.5 font-bold">Model</th>
              <th className="text-left pb-1.5 font-bold">Region</th>
              <th className="text-right pb-1.5 font-bold">Latency</th>
              <th className="text-right pb-1.5 font-bold">OK?</th>
            </tr>
          </thead>
          <tbody>
            {log.map((r, i) => {
              const isBedrock = r.provider === 'bedrock';
              const isGateway = ['ve_gateway', 'anthropic', 'gemini', 'openai'].includes(r.provider);
              const provColor = isBedrock ? 'text-emerald-400' : isGateway ? 'text-[var(--brand-primary)]' : 'text-rose-400';
              const provDisplay = r.provider === 've_gateway' ? 'VE Gateway' : (r.provider || 'unknown');
              return (
                <tr key={i} className="border-t border-slate-100 dark:border-white/5">
                  <td className="py-1.5 pal-mono">{r.persona}</td>
                  <td className={`py-1.5 pal-mono font-bold ${provColor}`}>{provDisplay}</td>
                  <td className="py-1.5 pal-mono opacity-70 break-all">{r.model || '—'}</td>
                  <td className="py-1.5 pal-mono opacity-70">{r.region || '—'}</td>
                  <td className="py-1.5 pal-mono text-right">{r.latency_ms ?? '—'} ms</td>
                  <td className="py-1.5 text-right">{r.ok ? <span className="text-emerald-400">✓</span> : <span className="text-rose-400">✗</span>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </details>
    </div>
  );
}
